import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';

/**
 * Searchable client picker built on Shadcn Command + Popover.
 *
 * Props:
 *  - clients: Array<{ id, company, name? }>
 *  - value: selected client id (string) or '' for none
 *  - onChange: (clientId: string, clientObj | null) => void
 *  - placeholder: trigger label
 *  - allowClear: show an X button to unset (default true)
 *  - testIdPrefix: prefix used to generate data-testid attrs
 *  - disabled
 */
export default function ClientCombobox({
  clients = [],
  value = '',
  onChange,
  placeholder = 'Pick a client',
  allowClear = true,
  testIdPrefix = 'client-combobox',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = clients.find((c) => c.id === value) || null;

  const handleSelect = (clientId) => {
    const c = clients.find((x) => x.id === clientId) || null;
    if (onChange) onChange(c ? c.id : '', c);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    if (onChange) onChange('', null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={`${testIdPrefix}-trigger`}
          className="w-full justify-between bg-[#0f2240] border-amber-500/20 hover:bg-[#0f2240] hover:border-amber-500/40 text-white font-normal"
        >
          <span className={selected ? 'truncate' : 'truncate text-slate-400'}>
            {selected ? selected.company : placeholder}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {allowClear && selected && (
              <span
                role="button"
                tabIndex={0}
                onClick={clear}
                onKeyDown={(e) => { if (e.key === 'Enter') clear(e); }}
                data-testid={`${testIdPrefix}-clear`}
                className="text-slate-400 hover:text-red-300 cursor-pointer"
                title="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-60" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-[#0a1628] border-amber-500/20 text-slate-100"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder="Search clients…"
            className="text-slate-100 placeholder:text-slate-500"
            data-testid={`${testIdPrefix}-search`}
          />
          <CommandList>
            <CommandEmpty className="text-slate-400 py-4 text-sm">No clients found.</CommandEmpty>
            <CommandGroup>
              {clients.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.company} ${c.name || ''} ${c.email || ''} ${c.phone || ''}`}
                  onSelect={() => handleSelect(c.id)}
                  data-testid={`${testIdPrefix}-option-${c.id}`}
                  className="text-slate-100 aria-selected:bg-amber-500/15 aria-selected:text-white cursor-pointer"
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c.id ? 'opacity-100 text-emerald-400' : 'opacity-0'}`} />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium">{c.company}</span>
                    {c.name && <span className="text-[11px] text-slate-400 truncate">{c.name}</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
